import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { GuideMenu, LoadingState, LoginPage, ProductWordmark, SystemIcon, UserMenu, useTheme } from "@ips/ui-kit";
import type { IdentityContext, RegisteredModule } from "./contracts";
import { navigate, usePath } from "./router";
import { ModuleErrorBoundary } from "./ModuleErrorBoundary";
import { routeIconDefaults } from "./menu";
import { guideAudioItems } from "./guide-audio";

export function App({
  identity,
  modules = [],
  readOnly = false,
}: {
  identity?: IdentityContext;
  modules?: RegisteredModule[];
  readOnly?: boolean;
}) {
  const [activeIdentity,setActiveIdentity]=useState(identity);
  const [loginError,setLoginError]=useState("");
  const [authPending,setAuthPending]=useState(!identity);
  const { preferences } = useTheme();
  const routes = modules
    .filter((m) => m.enabled)
    .flatMap((m) => m.routes ?? [])
    .filter((r) => activeIdentity?.permissions.includes(r.permission));
  const primaryRoutes = routes.filter(
    (route) => route.navigation !== "submenu",
  );
  const path = usePath();
  useEffect(()=>{
    if(identity)return;
    fetch("/api/core/v1/auth/me",{credentials:"include"}).then(async response=>{
      if(!response.ok)throw new Error();
      const result=await response.json() as {user:{username:string;displayName?:string;organizationId:string;siteIds:string[];permissions:string[]}};
      setActiveIdentity({userId:result.user.displayName??result.user.username,organizationId:result.user.organizationId,siteIds:result.user.siteIds,permissions:result.user.permissions});
    }).catch(()=>{setActiveIdentity(undefined);if(location.pathname!=="/login")navigate("/login")}).finally(()=>setAuthPending(false));
  },[identity]);
  useEffect(() => {
    if (path === "/") navigate("/dashboard");
  }, [path]);
  const selected =
    routes.find((r) => r.path === path) ??
    (path === "/" ? routes.find((r) => r.path === "/dashboard") : undefined);
  const Page = useMemo(
    () => (selected ? lazy(selected.load) : null),
    [selected],
  );
  const routeIcon = (path: string) =>
    preferences.menuIcons[path] ?? routeIconDefaults[path] ?? "table";
  if(authPending)return <LoadingState label="Verifica della sessione"/>;
  if(path==="/login")return <LoginPage
    pending={authPending}
    errorMessage={loginError}
    brandLogo={{src:"/brand/ips-engineering-transparent.png",alt:"IPS Engineering — Industrial Process Solutions"}}
    demoCredentials={{username:"ospite",password:"ospiteips2026"}}
    introductionAudio={{title:"Introduzione a Plant Manager",src:"/login-audio/presentazione-plant-manager.mp3"}}
    onSubmit={async(username,password)=>{setAuthPending(true);setLoginError("");try{const response=await fetch("/api/core/v1/auth/login",{method:"POST",credentials:"include",headers:{"content-type":"application/json"},body:JSON.stringify({username,password})});if(!response.ok)throw new Error();const result=await response.json() as {user:{username:string;displayName?:string;organizationId?:string;siteIds?:string[];permissions:string[]}};setActiveIdentity({userId:result.user.displayName??result.user.username,organizationId:result.user.organizationId??"IPS_PLANT_LOCAL",siteIds:result.user.siteIds??["IPS"],permissions:result.user.permissions});navigate("/dashboard")}catch{setLoginError("Nome utente o password non validi.")}finally{setAuthPending(false)}}
  }/>;
  if(!activeIdentity){navigate("/login");return <LoadingState label="Accesso richiesto"/>}
  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <nav aria-label="Moduli installati">
          {primaryRoutes.length ? (
            primaryRoutes.map((route) => (
              <a
                className={selected?.path === route.path ? "active" : ""}
                key={route.path}
                href={route.path}
                data-tooltip={route.label}
                aria-label={route.label}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(route.path);
                }}
              >
                <SystemIcon
                  name={routeIcon(route.path)}
                  size={preferences.menuIconSize}
                />
                <span className="sr-only">{route.label}</span>
              </a>
            ))
          ) : (
            <span className="nav-empty">—</span>
          )}
        </nav>
        <div
          className="sidebar-footer"
          data-tooltip="Sistema locale operativo"
          aria-label="Sistema locale operativo"
        >
          <span className="system-dot" />
        </div>
      </aside>
      <div className="app-workspace">
        <header className="app-topbar">
          <ProductWordmark version="V03" />
          <div className="topbar-tools">
            <GuideMenu items={guideAudioItems}/>
            <UserMenu displayName={activeIdentity.userId} role={readOnly?"Demo · sola consultazione":"Utente autenticato"} onLogout={readOnly?undefined:async()=>{await fetch("/api/core/v1/auth/logout",{method:"POST",credentials:"include"});setActiveIdentity(undefined);navigate("/login")}}/>
          </div>
        </header>
        <main>
          <ModuleErrorBoundary>
            {Page ? (
              <Suspense fallback={<LoadingState label="Caricamento modulo" />}>
                <Page />
              </Suspense>
            ) : (
              <section className="welcome">
                <header>
                  <h1>Pagina non disponibile</h1>
                  <p>Seleziona una voce dal menu laterale.</p>
                </header>
              </section>
            )}
          </ModuleErrorBoundary>
        </main>
      </div>
    </div>
  );
}
